import React, { useCallback, useContext, useEffect, useState } from "react";
import "./products.css";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Pagination,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import {
  collection,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  updateDoc,
  doc,
  addDoc,
  where,
  writeBatch,
  documentId,
} from "firebase/firestore";
import { db } from "../../../../firebase";
import { deleteDoc } from "firebase/firestore";
import { AsyncImage } from "loadable-image";
import CustomDialog from "../../../../components/CustomDialog/CustomDialog";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { debounce } from "lodash";
import { AppContext } from "../../../../store/AppContext";
import { ContentCopy } from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { CustomButton } from "../../../../components/CustomButton/CustomButton";
import {
  buildCategoryNameMap,
  productMatchesSearch,
  sortProductsForSearch,
} from "./productSearch";
import { searchProductsServer } from "../../../../api/products";
import { fetchProductsForCategory as fetchCategoryProducts } from "../../../../utils/categoryProducts";

/** Normalize createdDate (Timestamp | string | number) + createdAtMs → epoch ms. */
const getProductCreatedMs = (p) => {
  if (typeof p?.createdAtMs === "number" && Number.isFinite(p.createdAtMs)) {
    return p.createdAtMs;
  }
  const t = p?.createdDate;
  if (!t) return 0;
  if (typeof t.toMillis === "function") return t.toMillis();
  if (typeof t.seconds === "number") return t.seconds * 1000;
  if (typeof t === "string" || typeof t === "number") {
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }
  return 0;
};

const sortProductsNewestFirst = (list) =>
  [...list].sort((a, b) => {
    const byDate = getProductCreatedMs(b) - getProductCreatedMs(a);
    if (byDate !== 0) return byDate;
    return (a.position ?? 0) - (b.position ?? 0);
  });

/** Same order as storefront / Rearrange Products (lower position = higher). */
const sortProductsByPosition = (list) =>
  [...list].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

const ColorButton = styled(Button)(() => ({
  color: "white",
  fontSize: "1rem",
  borderRadius: "6px",
  boxShadow: "0px 3px 8px rgba(0, 123, 255, 0.3)",
  background: "linear-gradient(to right, #007BFF, #0056D2)",
  "&:hover": {
    background: "linear-gradient(to right, #0056D2, #003DA6)",
  },
}));

const Products = () => {
  const { setProductsTabInfo, productsTabInfo } = useContext(AppContext);
  const { setHeaderData, setLoader } = useContext(AppContext);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pageDocs, setPageDocs] = useState([null]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalProducts, setTotalProducts] = useState(0);
  const [rearrangeDialogOpen, setRearrangeDialogOpen] = useState(false);
  const [productsToRearrange, setProductsToRearrange] = useState([]);
  const [isRearranging, setIsRearranging] = useState(false);
  const pageSize = 10;
  const [selectedProductsForMove, setSelectedProductsForMove] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSelectForMove = (productId) => {
    setSelectedProductsForMove((prevSelected) => {
      if (prevSelected.includes(productId)) {
        return prevSelected.filter((id) => id !== productId);
      } else {
        return [...prevSelected, productId];
      }
    });
  };

  const moveToTop = () => {
    const productsToMove = productsToRearrange.filter((product) =>
      selectedProductsForMove.includes(product.id)
    );
    const remainingProducts = productsToRearrange.filter(
      (product) => !selectedProductsForMove.includes(product.id)
    );
    const newOrder = [...productsToMove, ...remainingProducts];
    setProductsToRearrange(newOrder);
    setSelectedProductsForMove([]);
  };

  useEffect(() => {
    setHeaderData((prevState) => ({
      ...prevState,
      leftText: "All products",
      back: true,
    }));
  }, []);

  const fetchCategories = async () => {
    try {
      const categoriesCollectionRef = collection(db, "categories");
      const categoriesQuery = query(
        categoriesCollectionRef,
        orderBy("categoryName")
      );
      const querySnapshot = await getDocs(categoriesQuery);
      const categoriesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoriesData);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchTotalProducts = async () => {
    const productsCollectionRef = collection(db, "products");
    const snapshot = await getDocs(productsCollectionRef);
    setTotalProducts(snapshot.size);
    setProductsTabInfo((prevState) => ({
      ...prevState,
      totalPages: Math.ceil(snapshot.size / pageSize),
    }));
  };

  const fetchProductsForCategory = async (
    _productsCollectionRef,
    selectedCategory
  ) => {
    // Chunked + deduped (parent includes subcategories).
    return fetchCategoryProducts(selectedCategory, categories);
  };

  const fetchProducts = async (page = 1) => {
    setLoader(true);
    try {
      const productsCollectionRef = collection(db, "products");

      if (searchTerm) {
        // Server search when available; always fall back to client match on
        // live fields if the API errors OR returns 0 (stale searchText on
        // older deploys hid real products like CHANDERI … 10519).
        const runClientSearch = async () => {
          let productPool;
          if (productsTabInfo?.selectedCategory) {
            productPool = await fetchProductsForCategory(
              productsCollectionRef,
              productsTabInfo.selectedCategory
            );
          } else {
            const allProductsSnapshot = await getDocs(productsCollectionRef);
            productPool = allProductsSnapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            }));
          }
          const categoryNameById = buildCategoryNameMap(categories);
          const matched = sortProductsForSearch(
            productPool.filter((product) =>
              productMatchesSearch(product, searchTerm, categoryNameById)
            ),
            searchTerm
          );
          setTotalProducts(matched.length);
          setProductsTabInfo((prevState) => ({
            ...prevState,
            totalPages: Math.max(1, Math.ceil(matched.length / pageSize)),
          }));
          const startIndex = (page - 1) * pageSize;
          setProducts(matched.slice(startIndex, startIndex + pageSize));
        };

        try {
          const result = await searchProductsServer({
            q: searchTerm,
            page,
            limit: pageSize,
            categoryId: productsTabInfo?.selectedCategory?.id || null,
          });
          if (!result.success) {
            throw new Error(result.error || "Search failed");
          }
          if ((result.total || 0) > 0) {
            setProducts(result.products || []);
            setTotalProducts(result.total || 0);
            setProductsTabInfo((prevState) => ({
              ...prevState,
              totalPages: Math.max(1, result.totalPages || 1),
            }));
            return;
          }
          // Server said no matches — verify with client (live name/SKU/…).
          await runClientSearch();
          return;
        } catch (searchErr) {
          console.warn(
            "Server product search failed, using client fallback:",
            searchErr
          );
          await runClientSearch();
          return;
        }
      } else if (productsTabInfo?.selectedCategory) {
        const allFilteredProducts = await fetchProductsForCategory(
          productsCollectionRef,
          productsTabInfo.selectedCategory
        );
        // Match rearrange / storefront: position asc (0 = top).
        const sortedCategory = sortProductsByPosition(allFilteredProducts);
        setTotalProducts(sortedCategory.length);
        setProductsTabInfo((prevState) => ({
          ...prevState,
          totalPages: Math.max(
            1,
            Math.ceil(sortedCategory.length / pageSize)
          ),
        }));
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        setProducts(sortedCategory.slice(startIndex, endIndex));
      } else {
        // All products (no category): load + sort by real created time.
        // Firestore orderBy(createdDate) is wrong here (mixed string/Timestamp);
        // orderBy(position) is also wrong (old "max+1" positions put newest last).
        const allSnap = await getDocs(productsCollectionRef);
        const sorted = sortProductsNewestFirst(
          allSnap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
        );
        setTotalProducts(sorted.length);
        setProductsTabInfo((prevState) => ({
          ...prevState,
          totalPages: Math.max(1, Math.ceil(sorted.length / pageSize)),
        }));
        const startIndex = (page - 1) * pageSize;
        setProducts(sorted.slice(startIndex, startIndex + pageSize));
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchTotalProducts();
  }, []);

  useEffect(() => {
    fetchProducts(productsTabInfo?.page || 1);
  }, [
    productsTabInfo?.page,
    searchTerm,
    productsTabInfo?.selectedCategory,
    productsTabInfo?.trigger,
  ]);

  useEffect(() => {
    if (searchTerm) {
      setProductsTabInfo((prevState) => ({
        ...prevState,
        page: 1,
      }));
    }
  }, [searchTerm]);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 500),
    []
  );

  const toggleActiveStatus = async (productId, currentStatus) => {
    try {
      setLoader(true);
      const productRef = doc(db, "products", productId);
      await updateDoc(productRef, { active: !currentStatus });
      setProducts((prevProducts) =>
        prevProducts.map((product) =>
          product.id === productId
            ? { ...product, active: !currentStatus }
            : product
        )
      );
    } catch (error) {
      console.error("Error updating product status:", error);
    } finally {
      setLoader(false);
    }
  };

  const IOSSwitch = styled((props) => (
    <Switch
      focusVisibleClassName=".Mui-focusVisible"
      disableRipple
      {...props}
    />
  ))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    "& .MuiSwitch-switchBase": {
      padding: 0,
      margin: 2,
      transitionDuration: "300ms",
      "&.Mui-checked": {
        transform: "translateX(16px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          backgroundColor: theme.palette.primary.main,
          opacity: 1,
          border: 0,
        },
      },
    },
    "& .MuiSwitch-thumb": {
      boxSizing: "border-box",
      width: 22,
      height: 22,
    },
    "& .MuiSwitch-track": {
      borderRadius: 26 / 2,
      backgroundColor: "#E9E9EA",
      opacity: 1,
    },
  }));

  const deleteProduct = async () => {
    setDialogOpen(false);
    if (!selectedProductId) return;
    setLoader(true);
    try {
      const productRef = doc(db, "products", selectedProductId);
      const productSnap = await getDocs(
        query(
          collection(db, "products"),
          where(documentId(), "==", selectedProductId)
        )
      );
      const productData = productSnap.docs[0]?.data();
      if (productData) {
        await addDoc(collection(db, "audit_logs"), {
          action: "delete",
          productId: selectedProductId,
          oldData: productData,
          timestamp: new Date(),
        });
      }
      // Keep category.products arrays in sync for any leftover UI that reads them.
      const linkedCategoryIds = productData?.categories || [];
      if (linkedCategoryIds.length > 0) {
        const batch = writeBatch(db);
        await Promise.all(
          linkedCategoryIds.map(async (categoryId) => {
            const catRef = doc(db, "categories", categoryId);
            const catSnap = await getDoc(catRef);
            if (!catSnap.exists()) return;
            const productsArr = (catSnap.data().products || []).filter(
              (id) => id !== selectedProductId
            );
            batch.update(catRef, {
              products: productsArr,
              productCount: productsArr.length,
            });
          })
        );
        await batch.commit();
      }
      await deleteDoc(productRef);
      setProducts((prev) =>
        prev.filter((product) => product.id !== selectedProductId)
      );
      setTotalProducts((prev) => prev - 1);
      setProductsTabInfo((prev) => ({
        ...prev,
        totalPages: Math.ceil((prev.totalPages * pageSize - 1) / pageSize),
      }));
      fetchProducts(productsTabInfo?.page || 1);
    } catch (error) {
      console.error("Error deleting product:", error);
    } finally {
      setLoader(false);
      setSelectedProductId(null);
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const reorderedProducts = Array.from(productsToRearrange);
    const [removed] = reorderedProducts.splice(result.source.index, 1);
    reorderedProducts.splice(result.destination.index, 0, removed);
    setProductsToRearrange(reorderedProducts);
  };

  console.log(productsToRearrange.length, "rearrange");

  const saveRearrangedProducts = async () => {
    setLoader(true);

    try {
      // Update by document id directly (skuId lookup can miss / collide).
      // Firestore batches max 500 writes — commit in chunks.
      const CHUNK = 450;
      for (let i = 0; i < productsToRearrange.length; i += CHUNK) {
        const batch = writeBatch(db);
        const slice = productsToRearrange.slice(i, i + CHUNK);
        slice.forEach((product, offset) => {
          const productId = product.id || product.docId;
          if (!productId) {
            console.warn("Rearrange skip: missing product id", product);
            return;
          }
          batch.update(doc(db, "products", productId), {
            position: i + offset,
          });
        });
        await batch.commit();
      }

      setRearrangeDialogOpen(false);
      setIsRearranging(false);
      fetchProducts(productsTabInfo?.page || 1);
    } catch (error) {
      console.error("Error saving rearranged products:", error);
    } finally {
      setLoader(false);
    }
  };

  const handleEdit = (data) => {
    setHeaderData((prevState) => ({
      ...prevState,
      back: false,
      leftText: "Update product info",
      leftAction: () => {
        setProductsTabInfo((prevState) => ({
          ...prevState,
          addProduct: false,
          selectedProduct: null,
        }));
        setHeaderData({
          leftText: "",
          rightActionText: "",
          leftAction: () => {},
          rightAction: () => {},
        });
      },
      rightActionText: "Update product",
      rightAction: () =>
        setProductsTabInfo((prevState) => ({
          ...prevState,
          trigger: !prevState.trigger,
        })),
    }));
    setProductsTabInfo((prevState) => ({
      ...prevState,
      selectedProduct: data,
    }));
  };

  const handleCopy = (data) => {
    setHeaderData((prevState) => ({
      ...prevState,
      back: false,
      leftText: "Add new product",
      leftAction: () => {
        setProductsTabInfo((prevState) => ({
          ...prevState,
          addProduct: false,
          selectedProduct: null,
        }));
        setHeaderData({
          leftText: "",
          rightActionText: "",
          leftAction: () => {},
          rightAction: () => {},
        });
      },
      rightActionText: "Add product",
      rightAction: () =>
        setProductsTabInfo((prevState) => ({
          ...prevState,
          trigger: !prevState.trigger,
        })),
    }));
    const productCopy = { ...data };
    delete productCopy.id;
    setProductsTabInfo((prevState) => ({
      ...prevState,
      selectedProduct: productCopy,
    }));
  };

  const handleDelete = (id) => {
    setSelectedProductId(id);
    setDialogOpen(true);
  };

  const renderProductContent = () => {
    // REDESIGNED Mobile Card View
    if (isMobile) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {products.map((data) => {
            const hasVariants =
              data.variantDetails && data.variantDetails.length > 0;
            const totalInventory = hasVariants
              ? data.variantDetails.reduce(
                  (acc, v) => acc + (v.inventory || 0),
                  0
                )
              : data.quantity || 0;
            const stockStatus = totalInventory > 0;

            return (
              <Card
                key={data.id}
                sx={{
                  borderRadius: 3,
                  transition:
                    "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  },
                }}
              >
                <Box sx={{ display: "flex", p: 2, gap: 2 }}>
                  <AsyncImage
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      flexShrink: 0,
                    }}
                    src={data?.images?.[0]}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flexGrow: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        onClick={() => handleEdit(data)}
                        sx={{
                          fontWeight: 600,
                          color: "text.primary",
                          cursor: "pointer",
                          fontSize: "1.05rem",
                          lineHeight: 1.3,
                          "&:hover": { color: "primary.main" },
                        }}
                      >
                        {data?.productName}
                      </Typography>
                      <IOSSwitch
                        checked={data.active || false}
                        onChange={() =>
                          toggleActiveStatus(data.id, data.active)
                        }
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5, fontWeight: 500 }}
                    >
                      ₹{data?.productDiscountedPrice ?? data.productPrice}
                    </Typography>
                    {data.variantDetails && data.variantDetails.length > 0 ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        {data.variantDetails.map((variant, index) => {
                          const sizeOption = variant.combination?.find(
                            (opt) => opt.name.toLowerCase() === "size"
                          );
                          const colorOption = variant.combination?.find(
                            (opt) => opt.name.toLowerCase() === "color"
                          );
                          return (
                            <div
                              key={index}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              {colorOption && (
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    minWidth: "30px",
                                  }}
                                >
                                  {typeof colorOption.originalValue === "object"
                                    ? colorOption.originalValue.name
                                    : colorOption.originalValue}
                                </span>
                              )}
                              {sizeOption && (
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    minWidth: "30px",
                                  }}
                                >
                                  {sizeOption.originalValue || "N/A"}:
                                </span>
                              )}
                              <span style={{ minWidth: "40px" }}>
                                {variant.inventory || 0}
                              </span>
                              <span
                                style={{
                                  fontSize: "12px",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  color:
                                    variant.inventory > 0
                                      ? "#28a745"
                                      : "#dc3545",
                                  border: `1px solid ${
                                    variant.inventory > 0
                                      ? "#28a745"
                                      : "#dc3545"
                                  }`,
                                }}
                              >
                                {variant.inventory > 0
                                  ? "In Stock"
                                  : "Out of Stock"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span style={{ minWidth: "40px" }}>
                          {data.quantity || 0}
                        </span>
                        <span
                          style={{
                            fontSize: "12px",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            color: data.quantity > 0 ? "#28a745" : "#dc3545",
                            border: `1px solid ${
                              data.quantity > 0 ? "#28a745" : "#dc3545"
                            }`,
                          }}
                        >
                          {data.quantity > 0 ? "In Stock" : "Out of Stock"}
                        </span>
                      </div>
                    )}
                  </Box>
                </Box>
                <Divider />
                <CardActions sx={{ justifyContent: "space-around", py: 0.5 }}>
                  <IconButton
                    size="small"
                    aria-label="view"
                    onClick={() =>
                      window.open(
                        `https://kathys-new-alpha.vercel.app/product/${data.id}`,
                        "_blank"
                      )
                    }
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="copy"
                    onClick={() => handleCopy(data)}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="delete"
                    onClick={() => handleDelete(data.id)}
                  >
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            );
          })}
        </Box>
      );
    }

    // UNTOUCHED Desktop Table View
    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          background: "white",
          borderRadius: "10px",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          padding: "10px",
          height: "max-content",
          overflowY: "scroll",
        }}
      >
        <div style={{ borderRadius: "8px", overflow: "hidden" }}>
          <table style={{ width: "100%", fontSize: "14px" }}>
            <thead>
              <tr
                style={{
                  background: "#efefef",
                  height: "50px",
                  borderRadius: "8px",
                }}
              >
                <th
                  className="product-name"
                  style={{ fontSize: "14px", fontWeight: "500" }}
                >
                  Products
                </th>
                <th style={{ fontSize: "14px", fontWeight: "500" }}>Price</th>
                <th style={{ fontSize: "14px", fontWeight: "500" }}>
                  Inventory
                </th>
                <th style={{ fontSize: "14px", fontWeight: "500" }}>Status</th>
                <th style={{ fontSize: "14px", fontWeight: "500" }}>Action</th>
              </tr>
            </thead>
            <tbody className="scrollable-tbody">
              {products.map((data) => (
                <tr
                  key={data.id}
                  className="table-row"
                  style={{ height: "70px" }}
                >
                  <td className="product-name">
                    <div
                      style={{
                        display: "flex",
                        width: "150px",
                        gap: "15px",
                        alignItems: "center",
                      }}
                    >
                      <AsyncImage
                        style={{
                          width: "50px",
                          height: "50px",
                          objectFit: "cover",
                          objectPosition: "top",
                          borderRadius: "4px",
                        }}
                        src={data?.images?.[0]}
                      />
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          alignItems: "flex-start",
                          overflow: "hidden",
                          textAlign: "start",
                        }}
                      >
                        <h5
                          style={{
                            fontWeight: "500",
                            color: "#00A3FF",
                            cursor: "pointer",
                            fontSize: "14px",
                            margin: 0,
                          }}
                          onClick={() => handleEdit(data)}
                        >
                          {data?.productName}
                        </h5>
                        <h5
                          style={{
                            fontWeight: "500",
                            fontSize: "14px",
                            margin: 0,
                          }}
                        >
                          {data.productCategory}
                        </h5>
                      </div>
                    </div>
                  </td>
                  <td className="product-price" style={{ fontSize: "14px" }}>
                    {data?.productDiscountedPrice
                      ? data?.productDiscountedPrice
                      : data.productPrice}
                  </td>
                  <td
                    className="product-inv"
                    style={{
                      fontSize: "14px",
                      textAlign: "left",
                      paddingLeft: "20px",
                    }}
                  >
                    {data.variantDetails && data.variantDetails.length > 0 ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        {data.variantDetails.map((variant, index) => {
                          const sizeOption = variant.combination?.find(
                            (opt) => opt.name.toLowerCase() === "size"
                          );
                          const colorOption = variant.combination?.find(
                            (opt) => opt.name.toLowerCase() === "color"
                          );
                          return (
                            <div
                              key={index}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              {colorOption && (
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    minWidth: "30px",
                                  }}
                                >
                                  {typeof colorOption.originalValue === "object"
                                    ? colorOption.originalValue.name
                                    : colorOption.originalValue}
                                </span>
                              )}
                              {sizeOption && (
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    minWidth: "30px",
                                  }}
                                >
                                  {sizeOption.originalValue || "N/A"}:
                                </span>
                              )}
                              <span style={{ minWidth: "40px" }}>
                                {variant.inventory || 0}
                              </span>
                              <span
                                style={{
                                  fontSize: "12px",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  color:
                                    variant.inventory > 0
                                      ? "#28a745"
                                      : "#dc3545",
                                  border: `1px solid ${
                                    variant.inventory > 0
                                      ? "#28a745"
                                      : "#dc3545"
                                  }`,
                                }}
                              >
                                {variant.inventory > 0
                                  ? "In Stock"
                                  : "Out of Stock"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span style={{ minWidth: "40px" }}>
                          {data.quantity || 0}
                        </span>
                        <span
                          style={{
                            fontSize: "12px",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            color: data.quantity > 0 ? "#28a745" : "#dc3545",
                            border: `1px solid ${
                              data.quantity > 0 ? "#28a745" : "#dc3545"
                            }`,
                          }}
                        >
                          {data.quantity > 0 ? "In Stock" : "Out of Stock"}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="product-action">
                    <IOSSwitch
                      checked={data.active || false}
                      onChange={() =>
                        toggleActiveStatus(data.id, data.active || false)
                      }
                    />
                  </td>
                  <td className="product-more">
                    <div
                      style={{
                        display: "flex",
                        gap: "15px",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <VisibilityIcon
                        style={{ fontSize: "20px" }}
                        onClick={() => {
                          window.open(
                            `https://kathys-new-alpha.vercel.app/product/${data.id}`,
                            "_blank"
                          );
                        }}
                      />
                      <ContentCopy
                        style={{ fontSize: "20px" }}
                        onClick={() => handleCopy(data)}
                      />
                      <DeleteOutlineOutlinedIcon
                        style={{ fontSize: "20px" }}
                        onClick={() => handleDelete(data.id)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
        maxWidth: "1200px",
        margin: "auto",
        p: { xs: 0, md: 3 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          p: { xs: 0, md: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <FormControl size="small" variant="outlined" sx={{ minWidth: 200 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={productsTabInfo?.selectedCategory?.id || ""}
              onChange={(e) => {
                const selectedId = e.target.value;
                const selectedCategory =
                  categories.find((c) => c.id === selectedId) || null;
                setProductsTabInfo((prev) => ({
                  ...prev,
                  selectedCategory,
                  page: 1,
                }));
                setPageDocs([null]);
                setSearchTerm("");
              }}
              label="Category"
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.categoryName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" variant="outlined" sx={{ minWidth: 260 }}>
            <InputLabel>Search name, SKU, size, price…</InputLabel>
            <OutlinedInput
              size="small"
              onChange={(e) => debouncedSearch(e.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <SearchOutlinedIcon />
                  </IconButton>
                </InputAdornment>
              }
              label="Search name, SKU, size, price…"
            />
          </FormControl>
          {productsTabInfo?.selectedCategory && (
            <ColorButton
              sx={{ textTransform: "none" }}
              variant="contained"
              onClick={async () => {
                setIsRearranging(true);
                setLoader(true);
                try {
                  const productsData = await fetchProductsForCategory(
                    collection(db, "products"),
                    productsTabInfo.selectedCategory
                  );
                  productsData.sort(
                    (a, b) => (a.position ?? 0) - (b.position ?? 0)
                  );
                  setProductsToRearrange(productsData);
                  setRearrangeDialogOpen(true);
                } catch (error) {
                  console.error(
                    "Error fetching products for rearrangement:",
                    error
                  );
                } finally {
                  setLoader(false);
                }
              }}
            >
              Rearrange Products
            </ColorButton>
          )}
        </Box>
        <ColorButton
          sx={{ textTransform: "none" }}
          variant="contained"
          onClick={() => {
            setHeaderData((prevState) => ({
              ...prevState,
              back: false,
              leftText: "Add new product",
              leftAction: () => {
                setProductsTabInfo((prevState) => ({
                  ...prevState,
                  addProduct: false,
                  selectedProduct: null,
                }));
                setHeaderData({
                  leftText: "",
                  rightActionText: "",
                  leftAction: () => {},
                  rightAction: () => {},
                });
              },
              rightActionText: "Add product",
              rightAction: () =>
                setProductsTabInfo((prevState) => ({
                  ...prevState,
                  trigger: !prevState.trigger,
                })),
            }));
            setProductsTabInfo((prevState) => ({
              ...prevState,
              addProduct: true,
              selectedProduct: null,
            }));
          }}
        >
          Add new product
        </ColorButton>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ px: 1, pb: 1 }}
      >
        {totalProducts} product{totalProducts !== 1 ? "s" : ""}
        {productsTabInfo?.selectedCategory
          ? ` in ${productsTabInfo.selectedCategory.categoryName}`
          : ""}
      </Typography>

      {renderProductContent()}

      {totalProducts > pageSize && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, pb: 2 }}>
          <Pagination
            count={productsTabInfo?.totalPages || 1}
            page={productsTabInfo?.page || 1}
            onChange={(e, value) =>
              setProductsTabInfo((prevState) => ({ ...prevState, page: value }))
            }
            color="primary"
          />
        </Box>
      )}

      <CustomDialog
        open={dialogOpen}
        title="Delete"
        primaryButtonText="Delete"
        onPrimaryClick={deleteProduct}
        onSecondaryClick={() => setDialogOpen(false)}
        description="Are you sure you want to delete this product?"
        type="error"
      />
      <CustomDialog
        open={rearrangeDialogOpen}
        title="Rearrange Products"
        titleLeft={true}
        primaryButtonText="Save Order"
        onPrimaryClick={saveRearrangedProducts}
        onSecondaryClick={() => {
          setRearrangeDialogOpen(false);
          setIsRearranging(false);
          setSelectedProductsForMove([]);
        }}
        description={
          <div>
            {selectedProductsForMove.length > 0 && (
              <CustomButton
                variant="contained"
                sx={{
                  mb: 2,
                  textTransform: "none",
                  position: "absolute",
                  right: "2rem",
                  top: "2rem",
                }}
                onClick={moveToTop}
              >
                Move Selected to Top
              </CustomButton>
            )}
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="products">
                {(provided) => (
                  <ul
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{ listStyleType: "none", padding: 0 }}
                  >
                    {productsToRearrange.map((product, index) => (
                      <Draggable
                        key={product.id}
                        draggableId={product.id}
                        index={index}
                      >
                        {(provided) => (
                          <li
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              padding: "10px",
                              margin: "5px 0",
                              border: "1px solid #ddd",
                              borderRadius: "5px",
                              backgroundColor: "#f9f9f9",
                              display: "flex",
                              alignItems: "center",
                              gap: "20px",
                              ...provided.draggableProps.style,
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedProductsForMove.includes(
                                    product.id
                                  )}
                                  onChange={() =>
                                    handleSelectForMove(product.id)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                />
                              }
                              label=""
                              sx={{ margin: 0 }}
                            />
                            <span style={{ fontSize: "0.8em", color: "#666" }}>
                              {index + 1}
                            </span>
                            <AsyncImage
                              style={{
                                width: "40px",
                                height: "40px",
                                objectFit: "cover",
                                borderRadius: "3px",
                              }}
                              src={product?.images?.[0]}
                            />
                            <span style={{ fontSize: ".9rem" }}>
                              {product.productName}
                            </span>
                          </li>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        }
      />
    </Box>
  );
};

export default Products;
